// All routes needs to be also added into nginx:
// \PrismaCloud\spa_PrismaCloud\nginx\app.conf
import { QuestionnairePatientsTab } from '@shared/types/QuestionnairePatientsTabs';

/* Prefix of the link to open configuration page of a device. */
export const DeviceConfigurationPrefix = '/device-configuration/';
/* Prefix of the link to open account settings of a user. */
export const UserAccountPrefix = '/users/';
/* Prefix of the link to open the patient profile page. */
export const PatientProfilePrefix = '/patient/';

const PatientViewDeviceConfigurationTabQuery = 'tab=therapySettings';
const PatientViewDeviceDataTabQuery = 'tab=deviceData';
const PatientViewPatientDataTabQuery = 'tab=patientData';
const PatientViewRemindersTabQuery = 'tab=reminders';
const PatientViewTrendCurvesTabQuery = 'tab=trendCurves';
const PatientViewAnnualStatsTabQuery = 'tab=annualStats';
const PatientViewTherapyCalendarTabQuery = 'tab=therapyCalendar';
const PatientViewQuestionnairesTabQuery = 'tab=questionnaires';
const PatientViewAccessoriesTabQuery = 'tab=accessories';

export const routes = {
  Home: '/',
  Login: '/login',
  /* Link to open activate a user page.
    Received via e-mail. */
  ActivateUser: '/activate',
  /* Link to open the forgot password page */
  ForgottenPassword: '/forgottenPassword',
  /* Link if the token is expired/invalid.
    Used if a link which is sent via e-mail is not valid any more. */
  WrongToken: '/wrongToken',
  /* Link to set a new password. Received via e-mail. */
  ForgotPassword: '/forgotPassword/:token/:email',
  PatientsTherapy: '/therapy',
  PatientsAdminList: '/patientsAdminList',
  Questionnaires: '/questionnaires/*',
  QuestionnairesRoot: '/questionnaires',
  Questionnaire: '/questionnaires/:surveyId',
  QuestionnairePatients: '/questionnaires/:surveyId/patients',
  QuestionnairePatientAnswer: '/questionnaires/:surveyId/patients/:patientId/answers/:answerId',
  QuestionnaireQuestions: '/questionnaires/:surveyId/questions',
  QuestionnairePreview: '/questionnaires/:surveyId/preview',
  Devices: '/devices/*',
  /* Link to open the configuration of a device. */
  DeviceConfigurationLink: `${DeviceConfigurationPrefix}:configurationId`,
  OrganisationSettings: '/settings',
  MedicalConditionsPage: '/indications',
  /* Open the account settings of the current user. */
  CurrentUserAccount: '/account',
  UserList: '/users',
  /* Open the account settings of a user. */
  UserAccount: `${UserAccountPrefix}:id`,
  /* Link to open the sd-card upload page. */
  Upload: '/upload',
  /* Link to open the firmware download page. */
  Download: '/download',
  /* Link to open the organisation list.
    Only valid in case of portal. */
  OrganisationList: '/organisations',
  /* Link to open organisation pairings page */
  OrganisationPairing: '/organisation-pairing',
  /* Link to open the preregistered devices upload page. */
  PreregisteredDevicesUpload: '/upload-devices',
  /* Old link to patients page for redirection*/
  ObsoletePatients: '/sleep-monitoring',
  /* The firmware download help page */
  FirmwareDownloadHelp: '/download-help',
  /* Open the page with the patient info. */
  PatientView: `${PatientProfilePrefix}:id`,
  /* Link to In-App Information page */
  OrganisationBranding: '/branding',
  ListOfControls: '/list-of-controls/*',
  QuestionnaireEmailSettings: '/questionnaire-email-settings',
  Accessories: '/accessories',
};

export const allRoutes = '/*';

export const Links = {
  home: '/',
  login: '/login',
  patientView: (patientId: string) => {
    const url = `${PatientProfilePrefix}${patientId}`;
    return {
      deviceConfiguration: (configurationId?: string) => {
        const configId = configurationId ?? '';
        const params = new URLSearchParams({
          configId,
        });
        return url.concat(`?${PatientViewDeviceConfigurationTabQuery}&${params.toString()}`);
      },
      deviceData: url.concat(`?${PatientViewDeviceDataTabQuery}`),
      patientData: url.concat(`?${PatientViewPatientDataTabQuery}`),
      reminders: url.concat(`?${PatientViewRemindersTabQuery}`),
      trendCurves: url.concat(`?${PatientViewTrendCurvesTabQuery}`),
      annualStats: url.concat(`?${PatientViewAnnualStatsTabQuery}`),
      therapyCalendar: url.concat(`?${PatientViewTherapyCalendarTabQuery}`),
      questionnaires: (questionnaireType?: QuestionnairePatientsTab, shouldOpenAnswerModal?: boolean) => {
        const params = new URLSearchParams({
          questionnaireTab: questionnaireType ?? 'Answered',
          shouldOpenAnswerModal: `${shouldOpenAnswerModal ?? false}`,
        });
        return url.concat(`?${PatientViewQuestionnairesTabQuery}&${params.toString()}`);
      },
      accessories: url.concat(`?${PatientViewAccessoriesTabQuery}`),
    };
  },
  questionnaires: {
    root: '/questionnaires',
    patients: (surveyId: string) => `/questionnaires/${surveyId}/patients`,
    patientAnswer: (surveyId: string, patientId: string, answerId: string) =>
      `/questionnaires/${surveyId}/patients/${patientId}/answers/${answerId}`,
    questions: (surveyId: string) => `/questionnaires/${surveyId}/questions`,
    preview: (surveyId: string) => `/questionnaires/${surveyId}/preview`,
  },
  deviceConfiguration: (configurationId: string) => {
    return `${DeviceConfigurationPrefix}${configurationId}`;
  },
  activateUser: '/activate',
  forgottenPassword: '/forgottenPassword',
  wrongToken: '/wrongToken',
  forgotPassword: '/forgotPassword/:token/:email',
  patientsTherapy: '/therapy',
  patientsAdminList: '/patientsAdminList',
  devices: {
    root: '/devices',
    registered: '/devices/registered',
    deleted: '/devices/deleted',
    moved: '/devices/moved',
  },
  organisationSettings: '/settings',
  medicalConditionsPage: '/indications',
  currentUserAccount: '/account',
  userList: '/users',
  userAccount: (userId: string) => {
    return `${UserAccountPrefix}${userId}`;
  },
  upload: '/upload',
  download: (userId?: string) => {
    const url = '/download';
    if (userId) {
      const params = new URLSearchParams({
        userId,
      });
      return `${url}?${params.toString()}`;
    } else {
      return url;
    }
  },
  organisationList: '/organisations',
  organisationPairing: '/organisation-pairing',
  preregisteredDevicesUpload: '/upload-devices',
  obsoletePatients: '/sleep-monitoring',
  firmwareDownloadHelp: '/download-help',
  organisationBranding: '/branding',
  listOfControls: {
    root: '/list-of-controls',
    buttons: '/list-of-controls?tab=buttons',
    fields: '/list-of-controls?tab=fields',
    reduxFields: '/list-of-controls?tab=reduxFields',
    reactHookFormFieldsInline: '/list-of-controls?tab=reactHookFormFieldsInline',
    reactHookFormFieldsModal: '/list-of-controls?tab=reactHookFormFieldsModal',
    icons: '/list-of-controls?tab=icons',
    modals: '/list-of-controls?tab=modals',
    other: '/list-of-controls?tab=other',
  },
  notSupported: '/not-supported/index.html',
  questionnaireEmailSettings: '/questionnaire-email-settings',
  accessories: '/accessories',
};
